import React, { useCallback, useState } from 'react'
import { Button, LinearBottom, SnackBar } from '@app/components/general'
import { metaSetter } from '@app/utils'
import { Header2 } from '@app/components/general/header'
import Papa from 'papaparse'
import { AppManager } from '@app/managers'
import { AuthContainer } from '@app/components/containers/auth-container'
import { useAuthContext } from '@app/components/providers/auth'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { IssueFeed } from '@app/components/feed'
import { useWasmContext } from '@app/components/providers'
import { useWebsiteContext } from '@app/components/providers/website'
import { BottomMenu } from '@app/components/general/navigation/bottom'
import { classNames } from '@app/utils/classes'
import { AppLayout } from '@app/components/app-layout'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/visualizer',
    }),
  }
}

// default issue data from issues CSV
const defaultIssues = { DOC_TITLE: '', DOMAIN: '', URL: '', ISSUES: '' }

const defaultFormCSS =
  'px-3 py-3 border-2 border-gray-800 dark:border-gray-400 space-y-4 rounded'

function DataParser() {
  const { account } = useAuthContext()
  const { t } = useTranslation('data-parse')
  const { feed } = useWasmContext()
  const { setIssueFeedContent, forceUpdate } = useWebsiteContext()

  const [loading, setLoading] = useState<boolean>(false)

  const validCSVText = t('valid-csv')
  const finishedText = t('complete')

  const updateUI = useCallback(() => {
    setIssueFeedContent(true)
    forceUpdate()
  }, [forceUpdate, setIssueFeedContent])

  const onParseCSV = useCallback(
    async (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault()
      const target = e.nativeEvent.target as HTMLInputElement

      const { files } = target
        ? (target['0' as keyof HTMLInputElement] as any)
        : { files: [] }

      if (!files || (files && !files.length)) {
        return AppManager.toggleSnack(true, validCSVText, 'error')
      }

      // first tick update
      updateUI()

      for (let i = 0; i < files.length; i++) {
        const file: File = files[i]

        if (!file) {
          continue
        }

        // csv
        const isCSV = file.type === 'text/csv'
        // json
        const isJson = !isCSV && file.type === 'application/json'

        const fileNotSupport = !isCSV && !isJson

        // TODO: alert file not supported in snackbar
        if (fileNotSupport) {
          continue
        }

        // CSV FROM A11YWATCH SAFE
        if (isCSV) {
          setLoading(true)

          // if json unparse
          Papa.parse(file, {
            worker: true,
            header: true,
            step: function (row) {
              const {
                DOMAIN,
                URL: _URL,
                ISSUES,
              } = (row.data as typeof defaultIssues) ?? defaultIssues

              if (DOMAIN) {
                let issues = null

                if (ISSUES) {
                  try {
                    issues = JSON.parse(ISSUES)
                    issues = 'data' in issues ? issues.data : issues
                  } catch (e) {
                    console.error(e)
                  }
                }

                feed.insert_website({
                  domain: DOMAIN,
                  pageUrl: _URL,
                  // title: DOC_TITLE,
                  issues: issues ?? [],
                })
              }
            },
            complete: function () {
              updateUI()
              setLoading(false)

              return AppManager.toggleSnack(true, finishedText)
            },
          })
          return
        }

        // todo: file reader stream chunks into WORKER
        if (isJson) {
          // if the file is small process as chunks
          const smallFile = file.size < 4459588
          setLoading(true)
          const decoder = new TextDecoder('utf-8')

          // if the file under 10mb process instantly
          if (smallFile) {
            const reader = new FileReader()

            reader.addEventListener('load', (event) => {
              const result = event.target && event.target.result

              if (result) {
                let data = null

                try {
                  data = JSON.parse(
                    decoder.decode(new Uint8Array(result as ArrayBuffer))
                  )
                } catch (e) {
                  console.error(e)
                }

                if (data) {
                  const chunkItem = 'data' in data ? data.data : data

                  for (const item of chunkItem) {
                    if (!item.pageUrl && item.url) {
                      item.pageUrl = item.url
                    }
                    feed.insert_website(item)
                  }

                  updateUI()
                }
              }
            })

            reader.readAsArrayBuffer(file)
          } else {
            // stream to worker todo
            const readableStream = file.stream().getReader()

            let jsonText = ''
            let previousChunkIncomplete = false

            while (true) {
              const { value, done } = await readableStream.read()

              // exit the loop
              if (done) {
                break
              }

              const chunkText = decoder.decode(value, { stream: true })

              // chunk did not complete
              if (previousChunkIncomplete) {
                jsonText += chunkText
                let nextObject

                try {
                  nextObject = JSON.parse(jsonText)
                  nextObject =
                    'data' in nextObject ? nextObject.data : nextObject

                  for (const item of nextObject) {
                    if (!item.pageUrl && item.url) {
                      item.pageUrl = item.url
                    }
                    feed.insert_website(item)
                  }

                  updateUI()

                  jsonText = ''
                  previousChunkIncomplete = false
                } catch (e) {
                  continue
                }
              } else {
                try {
                  const chunkJson = JSON.parse(chunkText)
                  const chunkItem =
                    'data' in chunkJson ? chunkJson.data : chunkJson

                  for (const item of chunkItem) {
                    if (!item.pageUrl && item.url) {
                      item.pageUrl = item.url
                    }
                    feed.insert_website(item)
                  }

                  updateUI()
                } catch (e) {
                  previousChunkIncomplete = true
                  jsonText = chunkText
                  continue
                }
              }
            }
          }

          setLoading(false)
        }
      }
    },
    [validCSVText, finishedText, feed, updateUI, setLoading]
  )

  return (
    <AuthContainer
      translation={'data-parse'}
      pageTitle={'header'}
      pageSubtitle='subtitle-details'
      user={account}
    >
      <div className='py-12 space-y-3 min-h-[60vh] overflow-hidden'>
        <Header2 head3 className='sr-only'>
          {t('subtitle')}
        </Header2>
        <form onSubmit={onParseCSV} noValidate className='grid gap-2'>
          <div>
            <label className='block text-sm font-semibold' htmlFor='csv'>
              {t('input-csv')} | JSON
            </label>
            <input
              type='file'
              id='csv'
              multiple
              accept='.json, .csv'
              className={classNames(
                defaultFormCSS,
                'w-full hover:border-gray-400 dark:hover:border-gray-200 border'
              )}
            />
          </div>

          <Button
            type='submit'
            className='min-w-[15rem] border border-gray-700 dark:border-gray-300 hover:text-blue-600 hover:border-blue-600 dark:hover:border-blue-600'
            border={false}
          >
            {t('submit')}
          </Button>
        </form>
        <IssueFeed small hideSync />
      </div>
      <BottomMenu active='visualizer' />
      <LinearBottom loading={loading} />
      <SnackBar topLevel />
    </AuthContainer>
  )
}

DataParser.getLayout = AppLayout

export default metaSetter(
  { DataParser },
  {
    translation: 'data-parse', // todo: match page name
    gql: true,
    wasm: true,
  }
)
